import React, { useEffect, useState, useRef, useReducer } from 'react';
import { bool, func, shape, string } from 'prop-types';
import {
  customType,
  extend,
  string as stringType,
  params,
  useLazyDataModel
} from '@thd-nucleus/data-sources';
import {
  Button,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerFooter,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { Locked, Settings } from '@one-thd/sui-icons';
import { ShareToggle } from '../ShareToggle/ShareToggle';
import { LIST_SHAPE, triggerAnalytics, validateListName } from '../helpers';
import {
  privateAccessType,
  sharedAccessType,
  LIST_SETTINGS_SAVE,
  LIST_DELETE,
  LIST_ALREADY_EXISTS_ERROR,
  LIST_SETTINGS_FORM_LOAD
} from '../constants';

export const ListSettings = (props) => {
  const {
    list, onClose, onDelete, onSave, iconSize
  } = props;

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('ListSettings.ready', {});
  }, []);

  const {
    id: listId,
    name: initialName,
    listAccessType: initialListAccessType
  } = list || {};

  const [currentSavedName, setCurrentSavedName] = useState(initialName);

  const initialValues = {
    listName: currentSavedName,
    listNameError: false,
    listNameValid: false,
  };

  const initialShared = initialListAccessType === sharedAccessType;
  const settingsForm = useRef();
  const [shared, setShared] = useState(initialShared);
  const [disableSave, setDisableSave] = useState(true);

  const [saveList, saveResponse] = useLazyDataModel('updateList', {
    variables: {
      id: listId
    },
    context: { withAuth: true }
  });

  const [saveShareToggle, saveShareToggleResponse] = useLazyDataModel('updateList', {
    variables: {
      id: listId
    },
    context: { withAuth: true }
  });

  const [submitDeleteList, deleteResponse] = useLazyDataModel('deleteList', {
    variables: {
      id: listId
    },
    context: { withAuth: true }
  });

  const [formValues, setFormValues] = useReducer(
    (currentFormValues, newFormValues) => ({ ...currentFormValues, ...newFormValues }), initialValues
  );

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = (resetValues = true) => {
    setOpen(false);
    const name = formValues.listName;
    setFormValues({
      listName: resetValues ? initialValues.listName : name,
      listNameError: false,
      listNameValid: false,
    });
    onClose();
  };

  useEffect(() => {
    if (open) {
      triggerAnalytics(LIST_SETTINGS_FORM_LOAD, {});
    }
  }, [open]);

  const {
    listName,
    listNameError,
    listNameErrorMessage,
    listNameValid
  } = formValues;

  const disableSaveButton = () => {
    setDisableSave(true);
  };

  const handleFormSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    disableSaveButton();
    const variables = {};
    if (listNameValid) {
      variables.name = listName;
      await saveList({
        variables,
        update: (cache, { data: responseData }) => {
          const { updateList } = responseData || {};
          if (updateList) {
            cache.modify({
              id: cache.identify(updateList),
              fields: {
                listAccessType() {
                  return shared ? sharedAccessType : privateAccessType;
                },
              }
            });
          }
        }
      });
    }
    onSave();
  };

  const handleListNameChange = (event) => {
    const { id: currentId, value } = event.target;
    const { isError, errorMessage } = validateListName(value);

    if (value === currentSavedName) {
      setFormValues({
        [currentId]: value,
        listNameValid: false,
        listNameError: false,
        listNameErrorMessage: null
      });
      return;
    }

    const truncated = value.slice(0, 50);
    setFormValues(
      {
        [currentId]: truncated,
        listNameValid: !isError,
        listNameError: isError,
        listNameErrorMessage: errorMessage
      }
    );
  };

  const handleShareToggle = async (event) => {
    event.preventDefault();
    const isShared = !shared;
    const variables = {
      listAccessType: isShared ? sharedAccessType : privateAccessType
    };
    await saveShareToggle({
      variables,
      update: (cache, { data: responseData }) => {
        const { updateList } = responseData || {};
        if (updateList) {
          cache.modify({
            id: cache.identify(updateList),
            fields: {
              name() {
                return initialName;
              }
            }
          });
        }
      }
    });
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    await submitDeleteList();
  };

  useEffect(() => {
    if (saveShareToggleResponse?.data?.updateList?.id) {
      const isShared = !shared;
      triggerAnalytics(LIST_SETTINGS_SAVE, {
        type: isShared ? 'share' : 'stop sharing'
      });
      setShared(!shared);
      onSave();
    }
  }, [saveShareToggleResponse?.data, saveShareToggleResponse?.error]);

  useEffect(() => {
    if (deleteResponse?.data?.deleteList && deleteResponse?.data?.deleteList.id) {
      onDelete({ listId });
      triggerAnalytics(LIST_DELETE, {});
      handleClose();
    }
  }, [deleteResponse?.data, deleteResponse?.error]);

  useEffect(() => {
    setDisableSave(!listNameValid);
  }, [listNameValid]);

  useEffect(() => {
    if (saveResponse?.data?.updateList && saveResponse?.data?.updateList?.name) {
      setCurrentSavedName(saveResponse?.data?.updateList?.name);
      const resetValues = false;
      handleClose(resetValues);
    } else if (saveResponse?.error) {
      const gqlErrors = saveResponse?.error?.graphQLErrors;
      let errorMessage = '';
      gqlErrors.forEach(function (err, index) {
        if (err?.message === LIST_ALREADY_EXISTS_ERROR) {
          errorMessage = LIST_ALREADY_EXISTS_ERROR;
        }
      });
      setFormValues({
        listNameValid: false,
        listNameError: true,
        listNameErrorMessage: errorMessage
      });
    }
  }, [saveResponse?.data, saveResponse?.error]);

  return (
    <div className="sui-flex sui-justify-center" data-component="ListSettings">
      <IconButton iconSize={iconSize} icon={Settings} alt="Settings" aria-label="Settings" onClick={handleOpen} />
      <Drawer open={open} onClose={handleClose}>
        <DrawerHeader title="List Settings" onClose={handleClose} />
        <DrawerBody data-component="ListSettings">
          <div className="sui-flex sui-gap-2 sui-mb-6">
            <Locked color="primary" />
            <Typography>
              Your list can only be seen by you or people you share it with
            </Typography>
          </div>
          <form
            className="sui-grid sui-gap-6"
            autoComplete="off"
            noValidate
            onSubmit={handleFormSubmit}
            ref={settingsForm}
          >
            <TextField
              required
              id="listName"
              label="List Name"
              type="text"
              fullWidth
              value={listName}
              onChange={handleListNameChange}
              status={listNameError ? 'error' : null}
              statusMessage={listNameError ? listNameErrorMessage : null}
            />
          </form>
          <ShareToggle
            isShared={shared}
            listId={listId}
            onShareToggle={handleShareToggle}
          />
        </DrawerBody>
        <DrawerFooter position="center">
          <div className="sui-mb-2">
            <Button
              variant="primary"
              fullWidth
              onClick={handleFormSubmit}
              disabled={disableSave}
              type="submit"
            > Save
            </Button>
          </div>
          <Button
            variant="secondary"
            fullWidth
            disabled={!listId || deleteResponse?.loading}
            onClick={handleDelete}
          > Delete List
          </Button>
        </DrawerFooter>
      </Drawer>
    </div>
  );

};

ListSettings.displayName = 'ListSettings';

ListSettings.propTypes = {
  onClose: func.isRequired,
  onDelete: func.isRequired,
  iconSize: string,
  list: shape({
    id: string,
    name: string,
    public: bool
  }).isRequired,
  onSave: func,
};

ListSettings.defaultProps = {
  onSave: () => { },
  iconSize: 'small'
};

ListSettings.dataModel = extend({
  updateList: params({
    id: stringType().isRequired(),
    name: stringType(),
    listAccessType: customType('AccessTypes').enum(['Private', 'Public', 'Shared'])
  }).mutation().shape(LIST_SHAPE),
  deleteList: params({
    id: stringType().isRequired()
  }).mutation().shape({
    id: stringType()
  })
});
